// Role-specific copy. Product is the default; VC is served on ?role=vc.
import { receipts as baseReceipts, marqueeItems as vcMarquee, thesis as baseThesis } from "./mock";

const orderReceipts = (order, overrides = {}) =>
  order.map((id) => ({ ...baseReceipts.find((r) => r.id === id), ...(overrides[id] || {}) }));

const product = {
  key: "product",
  meta: {
    title: "Tanmay Agarwal \u2014 Product Leader \u00b7 Imperial MBA \u201927 \u00b7 London",
    description:
      "Tanmay Agarwal \u2014 product leader. Six years building AI & SaaS products inside 17 startups, shipped to 150+ enterprises. Imperial MBA \u201927, open to senior product roles in London."
  },
  hero: {
    chip: "OPEN TO SENIOR PRODUCT ROLES",
    pre: "I ship",
    accent: "AI products",
    mid: "people",
    scribble: "actually use",
    oneLiner:
      "Six years building product inside seventeen startups. Shipped CLO \u2014 an AI nudging engine now used across 150+ enterprises. Turned a consulting practice into SaaS and took it to Singapore and the Middle East. Imperial MBA next \u2014 looking for senior product roles in London from August 2027.",
    cta: "Let\u2019s build something",
    chips: ["6 yrs in product", "17 startups", "150+ enterprises", "1M+ data points", "Imperial MBA \u201927"]
  },
  thesis: {
    eyebrow: "\u00a7 How I build",
    words: ["Discover.", "Ship.", "Scale."],
    meta: "03 CHAPTERS \u00b7 1 CRAFT",
    steps: [
      {
        id: "discover",
        kicker: "01",
        tag: "DISCOVER",
        title: "Find the real problem",
        bullets: ["1M+ data points analysed", "150+ orgs, one behaviour pattern", "Insight before roadmap", "Founders, CXOs & end users in the room"],
        color: "#FF8A5B"
      },
      {
        id: "ship",
        kicker: "02",
        tag: "SHIP",
        title: "Build what changes behaviour",
        bullets: ["CLO \u2014 AI nudging engine", "40\u201360% richer feedback", "60%+ less reviewer effort", "Consulting \u2192 SaaS (10xGoals)"],
        color: "#FFD2A6"
      },
      {
        id: "scale",
        kicker: "03",
        tag: "SCALE",
        title: "Take it further",
        bullets: ["Singapore \u00b7 KSA \u00b7 Qatar \u00b7 UAE", "5\u00d7 enterprise pipeline", "Imperial MBA \u201927", "Senior product roles \u00b7 London \u00b7 Aug \u201927"],
        color: "#FFF3E9"
      }
    ]
  },
  metrics: {
    blurb: "Every number ties to a shipped product or a real deployment. No vanity metrics.",
    items: [
      { stat: "150+", label: "Enterprise deployments", note: "India \u00b7 MENA \u00b7 SG" },
      { stat: "60%+", label: "Reviewer effort cut", note: "CLO \u00b7 AI nudging" },
      { stat: "1M+", label: "Data points modeled", note: "PeopleCues" },
      { stat: "5\u00d7", label: "Pipeline growth", note: "International GTM" }
    ]
  },
  receipts: orderReceipts(
    ["peoplecues", "global", "10xgoals", "pixis-insights", "ather", "pixis-fundraise", "iimb", "imperial", "onya"],
    {
      imperial: {
        line: "Sharpening the product leader: strategy, finance, a London network. FT top-25.",
        tagline: "Sharpening product leadership with strategy, finance and a London network",
        summary:
          "Full-time MBA at Imperial College Business School (Class of 2027). Top-25 globally per the Financial Times. The step from building products to leading product at scale in London.",
        sections: [
          {
            heading: "Why Imperial",
            body:
              "London is where I want to build next, and Imperial sits at the intersection of technology, design and business. Its tech-heavy ecosystem, product-minded peers and access to London's scale-ups made it the obvious pick."
          },
          {
            heading: "What I'm using it for",
            body:
              "Sharpening the strategy and commercial side of product \u2014 pricing, unit economics, go-to-market. Re-baselining the playbook I built in India against US, EU and Asian benchmarks, and building relationships across London's product community."
          },
          {
            heading: "What I'm targeting next",
            body:
              "Senior product roles in London from August 2027 \u2014 ideally AI-native or B2B SaaS teams where product shapes user behaviour, not just features."
          }
        ]
      },
      onya: {
        companyNote: "ONYA is an early-stage, founder-led consumer startup.",
        line: "Backed a founder-led startup. Sharpened how I judge what's worth building.",
        metric: "Angel",
        fullTitle: "Angel \u2014 ONYA",
        tagline: "Seeing product decisions from the other side of the table",
        summary:
          "Wrote an early-stage angel cheque into ONYA, a founder-led consumer startup. Evaluating it sharpened the product lens: is the problem real, is the product the right answer, and can the team ship fast enough?",
        sections: [
          {
            heading: "Approach",
            body:
              "Judged ONYA the way I judge any product: clarity of the problem, founder-market fit, and early signals that the product actually changes user behaviour. Six years inside 17 startups made it easier to spot what realistically scales."
          },
          {
            heading: "Key Learning",
            body: "The best product decisions start with choosing which problems are worth solving."
          }
        ]
      },
      "pixis-fundraise": {
        companyNote:
          "Chiratae Ventures (formerly IDG Ventures India) is a leading Indian investor and an early backer of Lenskart, Myntra and Uniphore."
      },
      iimb: {
        sections: [
          baseReceipts.find((r) => r.id === "iimb").sections[0],
          baseReceipts.find((r) => r.id === "iimb").sections[1],
          {
            heading: "Outcome",
            body:
              "Delivered to 50+ founders. The takeaway for me: articulating the operating system clearly \u2014 not just running it \u2014 is what lets a product leader scale beyond their own team."
          },
          baseReceipts.find((r) => r.id === "iimb").sections[3]
        ]
      }
    }
  ),
  marquee: [
    "PeopleCues \u00b7 150+ enterprises",
    "CLO \u00b7 AI nudging engine",
    "xto10x \u00b7 Binny Bansal",
    "Pixis \u00b7 AI marketing infra",
    "Ather Energy \u00b7 pre-IPO",
    "Meesho \u00b7 CRED \u00b7 Ather",
    "5\u00d7 international pipeline",
    "Imperial MBA \u201927",
    "IIM Bangalore \u00b7 NSRCEL",
    "Trinity Grade 6 Piano",
    "25+ countries"
  ],
  contact: {
    blurb: "Product role, a product problem to chew on, or just a coffee in London \u2014 pick a lane below. I reply within 48 hours.",
    intents: ["Product role", "Product chat", "Coffee", "Other"],
    emailPlaceholder: "you@company.com",
    tip: "Hiring? A link to the role + 2 lines on the problem space goes further than a long intro."
  },
  badge: "Open to senior product roles",
  footer:
    "Six years building product inside seventeen startups. London-based, Imperial MBA \u201927 \u2014 open to senior product roles from August 2027.",
  hobbiesItalic: "not shipping product."
};

const vc = {
  key: "vc",
  meta: {
    title: "Tanmay Agarwal \u2014 Operator turned Investor \u00b7 Imperial MBA \u201927 \u00b7 London",
    description:
      "Tanmay Agarwal \u2014 six years operating inside 17 startups, co-built a $17M Series B, angel investor in ONYA. Imperial MBA \u201927, looking for early-stage VC roles in London."
  },
  hero: {
    chip: "OPEN TO EARLY-STAGE VC ROLES",
    pre: "From",
    accent: "shipping products",
    mid: "to",
    scribble: "spotting founders",
    oneLiner:
      "Six years inside seventeen startups. Co-built a $17M Series B. Shipped AI products to 150+ enterprises. Wrote my first angel cheque. Imperial MBA next \u2014 looking for an early-stage VC seat in London from August 2027.",
    cta: "Pitch me, hire me",
    chips: ["6 yrs operating", "17 startups", "$17M raise", "1st angel cheque", "Imperial MBA \u201927"]
  },
  thesis: {
    eyebrow: baseThesis.eyebrow,
    words: ["Built.", "Backed.", "Next."],
    meta: "03 CHAPTERS \u00b7 1 DIRECTION",
    steps: [
      baseThesis.steps[0],
      baseThesis.steps[1],
      {
        ...baseThesis.steps[2],
        bullets: ["Imperial MBA \u201927", "Early-stage VC seat", "Sector-agnostic, stage-curious", "London, full-time from Aug \u201927"]
      }
    ]
  },
  metrics: {
    blurb: "Every number ties to a raised round, a real deployment, or a founder in the room. No vanity metrics.",
    items: [
      { stat: "$17M", label: "Series B raised", note: "Pixis \u00b7 led by Chiratae" },
      { stat: "25+", label: "Investor conversations", note: "Pixis Series B" },
      { stat: "17", label: "Startups from the inside", note: "Operator \u00b7 6 yrs" },
      { stat: "50+", label: "Founders coached", note: "IIM Bangalore \u00b7 NSRCEL" }
    ]
  },
  receipts: orderReceipts(["imperial", "onya", "pixis-fundraise", "ather", "peoplecues", "global", "10xgoals", "pixis-insights", "iimb"]),
  marquee: vcMarquee,
  contact: {
    blurb: "VC seat, pitch deck, or just a coffee in London \u2014 pick a lane below. I reply within 48 hours.",
    intents: ["VC role", "Pitch me", "Coffee", "Other"],
    emailPlaceholder: "you@fund.com",
    tip: "Pitching? A link to your deck + 2 lines on the problem goes further than a long intro."
  },
  badge: "Open to early-stage VC roles",
  footer:
    "Six years shipping startups. Now picking them. London-based, Imperial MBA \u201927 \u2014 open to early-stage VC seats from August 2027, writing angel cheques and advising founders in between.",
  hobbiesItalic: "not picking startups."
};

export const ROLES = { product, vc };
export const resolveRole = (param) => (param?.toLowerCase() === "vc" ? vc : product);
